import { LoadingOutlined, TeamOutlined } from '@ant-design/icons';
import { Dropdown, Modal, notification } from 'antd';
import {
  Button,
  Checkbox,
  DatePicker,
  Icon,
  Input,
  Select,
  Table,
  Tooltip,
} from 'components/common';
import LinkButton from 'components/common/LinkButton';
import ExportTemplatesModal from 'components/ExportTemplates/ExportTemplatesModal';
import dayjs from 'dayjs';
import { useAuth, useGet } from 'hooks';
import { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  exportTemplateActions as actions,
  exportTemplateSelectors as selectors,
} from 'reducers/exportTemplate/reducer';
import { exportTemplate } from 'services/api';
import { commonDateRanges } from 'services/inputTokens';
import { colours } from 'utils/theme';
import { useBulkDeleteTemplateMutation } from './hooks/useBulkDeleteTemplateMutation';
import { useDeleteTemplateMutation } from './hooks/useDeleteTemplateMutation';
import { useExportTemplateList } from './hooks/useExportTemplateList';
import { useTemplateMetadataFetch } from './hooks/useTemplateMetadataFetch';
import { listTab as strings } from './strings';
import { ActionButton, ActionsIcon, ActionsSpan, MoreButton } from './styled';
import { TemplateFavouriteButton } from './TemplateFavouriteButton';

/** @typedef {import('reducers/exportTemplate/state').SearchExportTemplateListItem} SearchExportTemplateListItem */

const baseTableHeaders = [
  { label: 'Template Name', key: 'name', minWidth: 150 },
  { label: ' ', minWidth: 40 }, // Favourite star
  { label: 'Number of Fields', key: 'numberOfFields', minWidth: 120 },
  { label: 'Owner', key: 'ownerName', minWidth: 140 },
  { label: 'Shared With', minWidth: 100 },
  { label: 'Last Updated', key: 'lastUpdated', minWidth: 120 },
  { label: 'Date Created', key: 'dateCreated', minWidth: 120 },
  { label: ' ', minWidth: 10 }, // Three-dot menu
];

/** @param {SearchExportTemplateListItem} template
 * @returns {boolean} template */
const isTemplateMine = template => template.sharedFromTemplateId == null;

/**
 * Export Template List Tab Component
 * Displays a table of user's export templates with search, filtering, and management actions.
 */
export const ExportTemplateListTab = () => {
  useTemplateMetadataFetch();
  const dispatch = useDispatch();
  const { hasLicense } = useAuth();
  const searchConfig = useSelector(selectors.getSearchConfiguration);
  const { templates, loading, totalCount } = useExportTemplateList();
  const doesSheetMetadataExist = useSelector(selectors.doesSheetMetadataExist);

  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [settingsLoadingId, setSettingsLoadingId] = useState(null);
  const [editLoadingId, setEditLoadingId] = useState(null);
  const { triggerDelete, deleteLoading } = useDeleteTemplateMutation(
    () => setTemplateToDelete(null),
    () => setTemplateToDelete(null)
  );

  // Multi-select state for bulk delete
  const [selectedIds, setSelectedIds] = useState({});
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);

  // Track which mode the fetch is for: 'edit' or 'settings'
  const fetchModeRef = useRef(null);

  const handleTemplateFetchSuccess = useCallback(
    httpData => {
      if (fetchModeRef.current === 'edit') {
        setEditLoadingId(null);
        dispatch(
          actions.existingTemplateEditStarted({
            response: httpData,
            hasLicense,
          })
        );
      } else if (fetchModeRef.current === 'settings') {
        setSettingsLoadingId(null);
        dispatch(actions.settingsModalOpened(httpData));
      }
      fetchModeRef.current = null;
    },
    [dispatch, hasLicense]
  );

  const handleTemplateFetchError = useCallback(() => {
    const mode = fetchModeRef.current;
    fetchModeRef.current = null;
    if (mode === 'edit') {
      setEditLoadingId(null);
      notification.error({
        message: strings.templateFetchErrorEdit,
        placement: 'bottomLeft',
        duration: 6,
      });
    } else if (mode === 'settings') {
      setSettingsLoadingId(null);
      notification.error({
        message: strings.templateFetchErrorSettings,
        placement: 'bottomLeft',
        duration: 6,
      });
    }
  }, []);

  const [, fetchTemplate] = useGet({
    onMount: false,
    configuration: {
      onRequestSuccess: handleTemplateFetchSuccess,
      onRequestError: handleTemplateFetchError,
    },
  });

  // ========== Search Filter Event handlers ==========

  const onSearchFilterDidChange = searchFilter =>
    dispatch(actions.searchConfigurationChanged({ searchFilter }));

  const onSearchDateOptionDidChange = searchDateOption =>
    dispatch(actions.searchConfigurationChanged({ searchDateOption }));

  const onSearchDateRangeDidChange = searchDateRange =>
    dispatch(actions.searchConfigurationChanged({ searchDateRange }));

  const onPageSizeDidChange = pageSize =>
    dispatch(actions.searchConfigurationChanged({ pageSize }));

  const onPageNumberDidChange = pageNumber =>
    dispatch(actions.searchConfigurationChanged({ pageNumber }));

  const onTableSortOrderDidChange = useCallback(
    orderBy => {
      let order =
        orderBy === searchConfig.orderBy ? searchConfig.order : 'desc';
      order = order === 'desc' ? 'asc' : 'desc';
      dispatch(actions.searchConfigurationChanged({ orderBy, order }));
    },
    [dispatch, searchConfig.order, searchConfig.orderBy]
  );

  // ========== Individual Template handlers ==========

  const handleConfirmDelete = () => {
    if (templateToDelete) {
      triggerDelete(templateToDelete.id);
    }
  };

  const handleCancelDelete = () => {
    setTemplateToDelete(null);
  };

  const onEditButtonDidPress = useCallback(
    templateId => {
      setEditLoadingId(templateId);
      fetchModeRef.current = 'edit';
      fetchTemplate({ url: exportTemplate(templateId) });
    },
    [fetchTemplate]
  );

  const onSettingsButtonDidPress = useCallback(
    templateId => {
      setSettingsLoadingId(templateId);
      fetchModeRef.current = 'settings';
      fetchTemplate({ url: exportTemplate(templateId) });
    },
    [fetchTemplate]
  );

  // ========== Multi-Select Handlers ==========

  const selectedCount = Object.keys(selectedIds).length;

  const selectableTemplateIds = useMemo(() => templates.map(t => t.id), [
    templates,
  ]);

  const allSelectableSelected = useMemo(() => {
    if (selectableTemplateIds.length === 0) return false;
    return selectableTemplateIds.every(id => selectedIds[id]);
  }, [selectableTemplateIds, selectedIds]);

  const someSelectableSelected = useMemo(() => {
    if (selectableTemplateIds.length === 0) return false;
    const selectedOnPage = selectableTemplateIds.filter(id => selectedIds[id]);
    return (
      selectedOnPage.length > 0 &&
      selectedOnPage.length < selectableTemplateIds.length
    );
  }, [selectableTemplateIds, selectedIds]);

  const onSelectAllDidChange = useCallback(
    checked => {
      setSelectedIds(prev => {
        const next = { ...prev };
        for (const id of selectableTemplateIds) {
          if (checked) next[id] = true;
          else delete next[id];
        }
        return next;
      });
    },
    [selectableTemplateIds]
  );

  const tableHeaders = useMemo(
    () => [
      {
        label:
          selectableTemplateIds.length > 0 ? (
            <Checkbox
              checked={allSelectableSelected}
              indeterminate={someSelectableSelected}
              onChange={onSelectAllDidChange}
            />
          ) : (
            ' '
          ),
        minWidth: 32,
      },
      ...baseTableHeaders,
    ],
    [
      selectableTemplateIds.length,
      allSelectableSelected,
      someSelectableSelected,
      onSelectAllDidChange,
    ]
  );

  const onTemplateSelectionDidChange = useCallback((templateId, checked) => {
    setSelectedIds(prev => {
      if (checked) {
        return { ...prev, [templateId]: true };
      }
      const next = { ...prev };
      delete next[templateId];
      return next;
    });
  }, []);

  const onDeselectAllDidPress = () => setSelectedIds({});

  const onBulkDeleteButtonDidPress = () => setBulkDeleteModalOpen(true);

  const onBulkDeleteComplete = () => {
    setBulkDeleteModalOpen(false);
    setSelectedIds({});
  };

  const {
    triggerBulkDelete,
    bulkDeleteLoading,
  } = useBulkDeleteTemplateMutation(onBulkDeleteComplete, onBulkDeleteComplete);

  const handleCancelBulkDelete = () => setBulkDeleteModalOpen(false);

  const handleConfirmBulkDelete = () => {
    const idsToDelete = Object.keys(selectedIds).map(Number);
    if (idsToDelete.length === 0) return;
    triggerBulkDelete(idsToDelete);
  };

  // ========== Generic Page Handlers ==========

  const handleCreateNew = () =>
    dispatch(actions.newTemplateEditStarted({ hasLicense }));

  const getDropdownItems = useCallback(
    template => {
      const items = [];
      if (isTemplateMine(template)) {
        items.push({
          key: 'settings',
          label:
            settingsLoadingId === template.id ? (
              <ActionsSpan>
                <LoadingOutlined />
                {strings.templateSettings}
              </ActionsSpan>
            ) : (
              <ActionsSpan>
                <ActionsIcon icon='settings' size='1x' />
                {strings.templateSettings}
              </ActionsSpan>
            ),
          onClick: () => onSettingsButtonDidPress(template.id),
          disabled: settingsLoadingId === template.id,
        });
      }
      items.push({
        key: 'delete',
        label: (
          <ActionsSpan data-testid={`template-delete-menu-item-${template.id}`}>
            <ActionsIcon icon='trash' size='1x' />
            {strings.templateDelete}
          </ActionsSpan>
        ),
        onClick: () => setTemplateToDelete(template),
      });
      return items;
    },
    [settingsLoadingId, onSettingsButtonDidPress]
  );

  const tableConfig = useMemo(
    () => ({
      headers: tableHeaders,
      /** @param {SearchExportTemplateListItem} template */
      createRow: template => (
        <Fragment key={template.id}>
          <div>
            <Checkbox
              data-testid={`template-checkbox-${template.id}`}
              checked={!!selectedIds[template.id]}
              onChange={checked =>
                onTemplateSelectionDidChange(template.id, checked)
              }
            />
          </div>
          <div
            data-testid={`template-name-${template.id}`}
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {template.name}
          </div>
          <TemplateFavouriteButton
            templateId={template.id}
            templateName={template.name}
          />
          <div>{template.availableFieldCount}</div>
          <div>{template.ownerName}</div>
          <div>
            {template.sharedWith.length > 0 ? (
              <Tooltip
                title={template.sharedWith.map(u => u.name).join(', ')}
                placement='right'
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    color: colours.grey,
                  }}
                >
                  <TeamOutlined style={{ fontSize: 16 }} />
                </div>
              </Tooltip>
            ) : (
              <div />
            )}
          </div>
          <div>{dayjs(template.lastUpdated).format('Do MMM YYYY')}</div>
          <div>{dayjs(template.dateCreated).format('Do MMM YYYY')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Dropdown
              menu={{ items: getDropdownItems(template) }}
              trigger={['click']}
              placement='bottomRight'
            >
              <MoreButton data-testid={`template-menu-button-${template.id}`}>
                <Icon size='1x' color={colours.text} icon='ellipsis' />
              </MoreButton>
            </Dropdown>
            <ActionButton
              size='small'
              onClick={() => onEditButtonDidPress(template.id)}
              disabled={editLoadingId === template.id}
            >
              {editLoadingId === template.id ? (
                <LoadingOutlined />
              ) : (
                strings.edit
              )}
            </ActionButton>
          </div>
        </Fragment>
      ),
    }),
    [
      tableHeaders,
      getDropdownItems,
      editLoadingId,
      onEditButtonDidPress,
      selectedIds,
      onTemplateSelectionDidChange,
    ]
  );

  return (
    <div>
      <ExportTemplatesModal />
      <Modal
        data-testid='template-delete-modal'
        open={!!templateToDelete}
        title={strings.deleteModalTitle(templateToDelete?.name)}
        onCancel={handleCancelDelete}
        onOk={handleConfirmDelete}
        okText={strings.confirm}
        okButtonProps={{
          disabled: deleteLoading,
          'data-testid': 'template-delete-confirm-button',
        }}
        cancelButtonProps={{
          disabled: deleteLoading,
          'data-testid': 'template-delete-cancel-button',
        }}
      >
        {strings.deleteConfirmBody(templateToDelete?.name)}
      </Modal>
      <Modal
        data-testid='bulk-template-delete-modal'
        open={bulkDeleteModalOpen}
        title={strings.deleteSelectedTitle}
        onCancel={handleCancelBulkDelete}
        onOk={handleConfirmBulkDelete}
        okText={strings.confirm}
        okButtonProps={{
          disabled: bulkDeleteLoading,
          'data-testid': 'bulk-template-delete-confirm-button',
        }}
        cancelButtonProps={{
          disabled: bulkDeleteLoading,
          'data-testid': 'bulk-template-delete-cancel-button',
        }}
      >
        {strings.bulkDeleteConfirmBody(selectedCount)}
      </Modal>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          margin: '16px 0',
        }}
      >
        <div style={{ fontSize: 13, color: colours.grey }}>
          {strings.description}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {selectedCount > 0 && (
            <Button
              data-testid='templates-delete-selected-button'
              style={{ display: 'flex', gap: 16, alignItems: 'center' }}
              onClick={onBulkDeleteButtonDidPress}
              disabled={loading}
            >
              <Icon icon='trash' size='1x' style={{ color: 'currentColor' }} />
              {strings.deleteSelected}
            </Button>
          )}
          <Button
            data-testid='templates-create-new-button'
            type='primary'
            onClick={handleCreateNew}
            disabled={!doesSheetMetadataExist}
          >
            {strings.createNewTemplate}
          </Button>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 25,
        }}
      >
        <Input
          onChange={onSearchFilterDidChange}
          placeholder={strings.searchPlaceholder}
          value={searchConfig.searchFilter}
          allowClear
          style={{ width: 300 }}
        />
        <Select
          options={commonDateRanges}
          placeholder={strings.dateRangePlaceholder}
          value={searchConfig.searchDateOption}
          onChange={onSearchDateOptionDidChange}
          allowClear
          style={{ width: 300 }}
        />
        {searchConfig.searchDateOption === 'custom' && (
          <DatePicker
            containerStyle={{ flex: 1 }}
            placeholder={[
              strings.startDatePlaceholder,
              strings.endDatePlaceholder,
            ]}
            type='range'
            onChange={onSearchDateRangeDidChange}
            value={searchConfig.searchDateRange}
          />
        )}
        {selectedCount > 0 && (
          <>
            <span style={{ fontSize: 13 }}>
              {strings.selectedCount(selectedCount)}
            </span>
            <LinkButton onClick={onDeselectAllDidPress}>
              {strings.deselectAll}
            </LinkButton>
          </>
        )}
      </div>

      <div style={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
        <Table
          data-testid='export-template-list-table'
          config={tableConfig}
          data={templates}
          loading={loading}
          pageSizeOptions={[
            { value: 10, label: '10 per page' },
            { value: 25, label: '25 per page' },
            { value: 50, label: '50 per page' },
          ]}
          resultSize={totalCount}
          onSortChange={onTableSortOrderDidChange}
          sort={searchConfig.orderBy}
          sortDir={searchConfig.order}
          pageSize={searchConfig.pageSize}
          onChangePageSize={onPageSizeDidChange}
          pageNumber={searchConfig.pageNumber}
          onChangePageNumber={onPageNumberDidChange}
          paginate
          sortable
        />
      </div>
    </div>
  );
};
